import style from "./style.module.scss";

import React, { ReactNode, useState } from "react";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Icon, jnjChevronDown, jnjChevronUp } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import classnames from "classnames";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { mdiChevronUp, mdiChevronDown } from "@mdi/js";
import _uniqueId from "lodash/uniqueId";

interface PropTypes {
    id: string;
    title: string | Element | JSX.Element;
    open?: boolean;
    color?: "primary" | "secondary" | "title";
    backgroundColor?: "white" | "grey";
    spacing?: 0 | "default";
    onClick?: (num: number) => void;
    children: ReactNode | ReactNode[];
    dataTestId?: string;
    border?: boolean;
    autoId?: string;
    shouldKeepContentMounted?: boolean;
    detailsId?: string;
    titleComponent?: string;
    wrappingTitle?: boolean;
}

export const Accordion = ({
    id,
    title,
    open = false,
    color = "secondary",
    backgroundColor = "grey",
    spacing = "default",
    onClick = null,
    children,
    dataTestId,
    border = true,
    autoId,
    shouldKeepContentMounted = false,
    detailsId = null,
    titleComponent = null,
    wrappingTitle = false,
}: PropTypes) => {
    const [isOpen, setIsOpen] = useState(open);
    const { jnjFullBranded } = useJnjBranding();

    const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const event = new CustomEvent("accordionClick", { ...e });
        e?.target?.dispatchEvent(event);
        setIsOpen(!isOpen);
        if (onClick) {
            const num = +id;
            onClick(num);
        }
        e?.preventDefault();
    };

    if (onClick && open !== isOpen) {
        setIsOpen(open);
    }

    const dataTestIdValue = dataTestId ?? `Accordion.${id}`;
    const summaryId = autoId ? autoId : _uniqueId("accordionId-");
    return (
        <details
            data-test-id={dataTestIdValue}
            className={classnames(style.element, style[`spacing_${spacing}`], border ? style.border : "")}
            open={isOpen}
            id={detailsId}
        >
            <summary
                data-test-id="Accordion.Title"
                id={summaryId}
                data-tracking-index={id}
                className={style.title}
                onClick={(e) => handleClick(e)}
                aria-controls={`${summaryId}.Region`}
                aria-expanded={isOpen}
                role="button"
            >
                <Typography
                    className={jnjFullBranded ? style.title__component : null}
                    color={jnjFullBranded ? "dark" : color}
                    weight={jnjFullBranded ? "400" : "500"}
                    variant="h5"
                    component={titleComponent}
                >
                    {title}
                </Typography>
                {jnjFullBranded ? (
                    <div
                        className={
                            jnjFullBranded ? classnames(style.toggle, wrappingTitle ? style.wrapingTitle : null) : null
                        }
                    >
                        {isOpen ? (
                            <Icon
                                color="black"
                                data-test-id={`${dataTestIdValue}-accordion_open`}
                                icon={jnjChevronUp}
                                size="small"
                                type="jnj"
                                verticalAlign
                            />
                        ) : (
                            <Icon
                                color="black"
                                data-test-id={`${dataTestIdValue}-accordion_closed`}
                                icon={jnjChevronDown}
                                size="small"
                                type="jnj"
                                verticalAlign
                            />
                        )}
                    </div>
                ) : isOpen ? (
                    <Icon data-test-id={`${dataTestIdValue}-accordion_open`} icon={mdiChevronUp} />
                ) : (
                    <Icon data-test-id={`${dataTestIdValue}-accordion_closed`} icon={mdiChevronDown} />
                )}
            </summary>
            <div
                id={`${summaryId}.Region`}
                data-test-id="Accordion.Content"
                data-keep-content-mounted={shouldKeepContentMounted}
                aria-labelledby={summaryId}
                className={classnames(style.content, style[backgroundColor], isOpen ? style.expanded : style.collapsed)}
                role="region"
            >
                {children}
            </div>
        </details>
    );
};
export default Accordion;
